import { validateZipCode } from '../../../utils/validations';
import { validateApplicableFractionValue } from '../../ManageBuildings/BuildingsTable/utils';
import { getSetAsideOptions } from '../utils';
import { ALPHANUMERIC_PATTERN, FUNDING_TYPE_OPTIONS } from '../constants';
import v1Messages from '../../ManageBuildings/messages';
import appMessages from '../../App/messages';
import messages from '../messages';
import type { Gates } from '../types';

const HALF = { xs: 12, sm: 6 };

export const buildFields = ({
  gates,
  states,
  intl,
  stateValue,
  cityValue,
  unresolvedNaurs,
  isNaurLoading,
  isNaurError,
  constructionTypeOptions,
}: {
  gates: Gates,
  states: { id: string, code: string }[],
  intl: Object,
  stateValue: ?string,
  cityValue: ?string,
  unresolvedNaurs: boolean,
  isNaurLoading: boolean,
  isNaurError: boolean,
  constructionTypeOptions: { value: string, text: string }[],
}) => {
  const {
    isAffordableProperty,
    isPHProperty,
    isRDProperty,
    isLIHTCProperty,
    showFractions,
    showFundingType,
    showCertOccConstructionType,
  } = gates;

  const text = (name: string, label: string, extra: Object = {}) => ({
    variant: 'text',
    name,
    label,
    GridProps: HALF,
    ...extra,
  });

  const alphanumeric = (maxLength: number) => ({
    rules: {
      maxLength,
      pattern: {
        value: ALPHANUMERIC_PATTERN,
        message: intl.formatMessage(messages.errorAlphanumeric),
      },
    },
  });

  // step keeps the browser from flagging a two-decimal fraction as a step mismatch; the
  // library's default '0' placeholder would read as 0% on a fraction that is not provided.
  const fraction = (name: string, label: string) => ({
    variant: 'number',
    name,
    label,
    step: 0.01,
    min: 0,
    max: 100,
    placeholder: '',
    disabled: unresolvedNaurs,
    rules: {
      validate: (value) =>
        validateApplicableFractionValue(value) ||
        intl.formatMessage(v1Messages.errorApplicableFractionInvalid),
    },
    GridProps: HALF,
  });

  return [
    text('streetAddress1', intl.formatMessage(messages.streetAddress), {
      rules: { required: true },
      trimStart: true,
    }),
    text('city', intl.formatMessage(messages.city), {
      rules: { required: true },
      trimStart: true,
    }),
    {
      variant: 'singleSelect',
      name: 'state',
      label: intl.formatMessage(messages.state),
      options: states.map(({ id, code }) => ({ value: id, text: code })),
      rules: { required: true },
      GridProps: HALF,
    },
    text('zipCode', intl.formatMessage(messages.zipCode), {
      rules: {
        required: true,
        validate: (value) =>
          validateZipCode(value) ||
          intl.formatMessage(v1Messages.errorInvalidZipCode),
      },
    }),
    text('buildingIDNumber', intl.formatMessage(v1Messages.buildingBIN)),
    {
      variant: 'date',
      name: 'placedInServiceDate',
      label: intl.formatMessage(v1Messages.buildingPISD),
      GridProps: HALF,
    },
    ...(isAffordableProperty
      ? [
          text('XMLSiteName', intl.formatMessage(v1Messages.xmlAltSiteName), {
            rules: { maxLength: 255 },
          }),
          {
            variant: 'switch',
            name: 'heraSpecial',
            label: intl.formatMessage(v1Messages.heraSpecial),
            GridProps: HALF,
          },
        ]
      : []),
    ...(isAffordableProperty && isLIHTCProperty
      ? [
          {
            variant: 'singleSelect',
            name: 'minimumSetAside',
            label: intl.formatMessage(v1Messages.minimumSetAside),
            options: getSetAsideOptions({ state: stateValue, city: cityValue }),
            GridProps: HALF,
          },
        ]
      : []),
    ...(isAffordableProperty && isRDProperty
      ? [text('RDSiteId', intl.formatMessage(v1Messages.rdSiteId))]
      : []),
    ...(isAffordableProperty && isPHProperty
      ? [
          text(
            'PHBuildingNumber',
            intl.formatMessage(v1Messages.phBuildingNumber),
            alphanumeric(6),
          ),
          text(
            'PHBuildingEntrance',
            intl.formatMessage(v1Messages.phBuildingEntrance),
            alphanumeric(3),
          ),
        ]
      : []),
    ...(showFundingType
      ? [
          {
            variant: 'singleSelect',
            name: 'fundingType',
            label: intl.formatMessage(v1Messages.fundingType),
            options: FUNDING_TYPE_OPTIONS,
            rules: { required: true },
            GridProps: HALF,
          },
        ]
      : []),
    ...(showFractions
      ? [
          // While the NAUR status loads the fractions stay locked, but with no banner: the
          // building may have no unresolved events at all.
          ...(unresolvedNaurs && !isNaurLoading
            ? [
                {
                  variant: 'banner',
                  status: isNaurError ? 'error' : 'warning',
                  message: intl.formatMessage(
                    isNaurError
                      ? v1Messages.errorNaurStatusLoadFailed
                      : v1Messages.disabledApplicableFractionTooltip,
                  ),
                  GridProps: { xs: 12 },
                },
              ]
            : []),
          fraction(
            'applicableFractionUnit',
            intl.formatMessage(v1Messages.applicableFractionUnit),
          ),
          fraction(
            'applicableFractionFloorSpace',
            intl.formatMessage(v1Messages.applicableFractionFloorSpace),
          ),
        ]
      : []),
    ...(showCertOccConstructionType
      ? [
          {
            variant: 'date',
            name: 'certificateOfOccupancyDate',
            label: intl.formatMessage(v1Messages.certificateOfOccupancyDate),
            GridProps: HALF,
          },
          {
            variant: 'singleSelect',
            name: 'constructionType',
            label: intl.formatMessage(v1Messages.constructionType),
            options: [
              { value: '', text: intl.formatMessage(appMessages.choose) },
              ...constructionTypeOptions,
            ],
            GridProps: HALF,
          },
        ]
      : []),
  ];
};
